/** Use of one plan limit; `max` null when the plan sets no limit. */
export interface PlanUsageValue {
  /** Null while the server cannot read the count. */
  current: number | null;
  max: number | null;
}

/** The server's answer for the settings of one automation. */
export interface AutomationPlanUsage {
  concurrentRuns: PlanUsageValue;
  webhook: {
    concurrentRuns: PlanUsageValue;
    automations: PlanUsageValue;
    runsPerMinute: PlanUsageValue;
    dataPerMinute: PlanUsageValue;
  } | null;
}

export type PlanUsageRowId =
  | 'concurrent-runs'
  | 'webhook-concurrent-runs'
  | 'webhook-automations'
  | 'webhook-runs-per-minute'
  | 'webhook-data-per-minute';

export interface PlanUsageRow {
  id: PlanUsageRowId;
  value: PlanUsageValue;
  /** `over` only after a downgrade: more is in use than the plan allows. */
  state: 'ok' | 'at-limit' | 'over';
}

function stateOf(value: PlanUsageValue): PlanUsageRow['state'] {
  if (value.current === null || value.max === null) return 'ok';
  if (value.current > value.max) return 'over';
  return value.current >= value.max ? 'at-limit' : 'ok';
}

/** One row per limit, in the order the settings show them. */
export function planUsageRows(usage: AutomationPlanUsage): PlanUsageRow[] {
  const values: Array<[PlanUsageRowId, PlanUsageValue]> = [
    ['concurrent-runs', usage.concurrentRuns],
    ...(usage.webhook
      ? ([
          ['webhook-concurrent-runs', usage.webhook.concurrentRuns],
          ['webhook-automations', usage.webhook.automations],
          ['webhook-runs-per-minute', usage.webhook.runsPerMinute],
          ['webhook-data-per-minute', usage.webhook.dataPerMinute],
        ] satisfies Array<[PlanUsageRowId, PlanUsageValue]>)
      : []),
  ];
  return values.map(([id, value]) => ({ id, value, state: stateOf(value) }));
}
